import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import SiteNav from "@/components/SiteNav";
import Footer from "@/components/Footer";

const DOMAINS = ["Indian languages", "Law", "Healthcare", "Coding", "Other specialised fields"];

export default function About() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#F6F3EC]">
      <SiteNav />

      <section className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pt-24 pb-20 sm:pt-32 sm:pb-28">
          <p className="eyebrow text-[#C79447] reveal">About FIAD</p>
          <h1 className="mt-6 font-display text-4xl sm:text-6xl lg:text-7xl font-semibold tracking-tight leading-[1.04] max-w-4xl reveal reveal-1">Better AI needs better human judgment.</h1>
        </div>
      </section>

      <section className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-20 sm:py-28 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16">
          <div className="lg:col-span-4">
            <p className="eyebrow text-[#71717A]">Mission</p>
          </div>
          <div className="lg:col-span-8 space-y-6 text-lg sm:text-xl text-[#D4D0C6] leading-relaxed">
            <p>AI systems increasingly depend on evaluation, feedback and judgment from people who understand the underlying domain.</p>
            <p>FIAD is building the structured human capability layer behind that work — sourcing specialised experts, training them for AI evaluation, assessing and vetting them against the demands of the work, and preparing qualified experts for relevant opportunities.</p>
          </div>
        </div>
      </section>

      <section className="border-b border-white/10 bg-[#0C0C0C]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-20 sm:py-28 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16">
          <div className="lg:col-span-4">
            <p className="eyebrow text-[#71717A]">Why it matters</p>
            <h2 className="mt-5 font-display text-3xl sm:text-4xl font-medium tracking-tight leading-[1.12]">Beyond generic crowdsourcing.</h2>
          </div>
          <div className="lg:col-span-8 lg:pt-2 space-y-5 text-lg text-[#A1A1AA] leading-relaxed">
            <p>Generic crowds can label data, but specialised evaluation asks harder questions. Is this legal reasoning sound? Is this clinical summary safe? Is this Bengali natural, accurate and appropriate?</p>
            <p>Those questions need people with real expertise — and a structure that turns their knowledge into consistent, explainable evaluation. That structure is what FIAD is building.</p>
          </div>
        </div>
      </section>

      <section className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-20 sm:py-28">
          <p className="eyebrow text-[#71717A]">Long-term domains</p>
          <h2 className="mt-5 font-display text-3xl sm:text-4xl font-medium tracking-tight max-w-2xl">Where FIAD is building toward.</h2>
          <div className="mt-12 grid grid-cols-2 sm:grid-cols-3 border-t border-l border-white/10">
            {DOMAINS.map((d, i) => (
              <div key={d} className="border-b border-r border-white/10 p-8 min-h-[130px] flex flex-col justify-between">
                <span className="font-mono-num text-xl text-white/15">0{i + 1}</span>
                <span className="font-display text-lg sm:text-xl font-medium tracking-tight text-[#F6F3EC]">{d}</span>
              </div>
            ))}
          </div>
          <p className="mt-6 text-sm text-[#71717A]">The current active domain is Bengali Linguistic Expertise. Other domains are areas FIAD is building toward.</p>
        </div>
      </section>

      <section>
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-20 sm:py-28">
          <h2 className="font-display text-3xl sm:text-4xl font-medium tracking-tight max-w-2xl">Want to build this with us — as an expert or an AI lab?</h2>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <button data-testid="about-founder-cta" onClick={() => navigate("/pilot")} className="inline-flex items-center justify-center gap-2 bg-[#C79447] text-[#0A0A0A] px-7 py-3.5 text-sm font-medium hover:brightness-110 transition-all">Discuss a Pilot <ArrowUpRight className="h-4 w-4" /></button>
            <button data-testid="about-experts-btn" onClick={() => navigate("/experts")} className="inline-flex items-center justify-center gap-2 border border-white/25 px-7 py-3.5 text-sm font-medium hover:border-white/60 transition-colors">Become an Expert</button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
